import { SKETCH_ENV_URL } from '@sketch/env-config'

/**
 * Resolves a `from` value to a full absolute URL for redirect after sign-in.
 *
 * - Relative paths (e.g. `/support/contact/`) are resolved against SKETCH_ENV_URL
 * - Absolute URLs are validated to match the current environment's origin
 * - Returns null if the URL is not allowed (different origin)
 */
export const resolveRedirectUrl = (from: string): string | null => {
  try {
    const resolved = new URL(from, SKETCH_ENV_URL)
    const { origin: allowedOrigin } = new URL(SKETCH_ENV_URL)

    if (resolved.origin !== allowedOrigin) {
      return null
    }

    return resolved.href
  } catch {
    return null
  }
}

/**
 * Checks if a URL is an absolute URL (has protocol).
 */
const isAbsoluteUrl = (url: string): boolean => {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

/**
 * Performs a full-page redirect to a URL derived from the `from` parameter.
 * Accepts both relative paths and absolute URLs on the current environment's domain.
 * Returns true if the redirect was performed, false otherwise.
 */
export const redirectExternalSafely = (from: string): boolean => {
  const url = resolveRedirectUrl(from)
  if (url) {
    window.location.href = url
    return true
  }
  return false
}

/**
 * Like redirectExternalSafely but only handles absolute URLs (with protocol).
 * Relative paths are ignored so they can be handled by React Router instead.
 *
 * Use this in social/SSO success views where `from` could be either an internal
 * SPA route (from PrivateRoute) or an external URL (from the `from` query param).
 */
export const redirectAbsoluteSafely = (from: string): boolean => {
  if (!isAbsoluteUrl(from)) {
    return false
  }
  return redirectExternalSafely(from)
}
